import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { diff } from "deep-diff";
import {
  APPLY_ALL,
  CLEAR_ALL,
  CLEAR_TECH_VALUES,
  type FacetToggleData,
  REMOVE_BREADCRUMB,
  SEARCH_SUCCESS,
  SELECT_PRODUCT_GROUP,
  SELECT_PRODUCT_GROUP_MULTI,
  SET_SELECTED_PRODUCT_GROUPS,
  TOGGLE_FACET,
  UPDATE_ASSISTANT_VALUES,
  UPDATE_IS_SELECTION_ASSISTANT,
  UPDATE_SEARCH_PARAMS_FROM_URL,
  UPDATE_SEARCH_TEXT,
} from "../actions";
import { RESET_SA_FILTERS } from "../../../BearingSelectionAssistant/redux/actions";
import {
  type BreadcrumbData,
  BreadcrumbType,
  type FacetBreadcrumb,
  type FacetData,
  type SearchResultData,
  type TechValueBreadcrumb,
} from "../../../utils/models";
import { type FilterConfig, isTechValueFilterCategory } from "~/Search/filterConfigModels";
import { type FromUrlSearchParams } from "../../utils/historyManipulation";
import { UnitMode } from "../../../feature/UnitMode/UnitMode";

export enum SelectionAssistantType {
  ROTARY = "ROTARY",
  HOUSINGS = "HOUSINGS",
  LINEAR = "LINEAR",
}

export interface FacetsState {
  facetValues: Record<string, FacetData>;
  assistantValues: string[];
  breadcrumbs: BreadcrumbData[];
  appliedTechFilterValues: { [code: string]: number };
  selectedProductGroups: string[];
  isSelectionAssistant?: SelectionAssistantType;
  filterConfig: FilterConfig;
  filterConfigImperial: FilterConfig;
}

export const facetsInitialState: FacetsState = {
  facetValues: {},
  assistantValues: [],
  breadcrumbs: [],
  appliedTechFilterValues: {},
  selectedProductGroups: [],
  filterConfig: [],
  filterConfigImperial: [],
};

function getUom(title: string) {
  return title.substring(title.lastIndexOf("(") + 1, title.lastIndexOf(")"));
}

export const facetsSlice = createSlice({
  name: "facets",
  initialState: facetsInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(UPDATE_SEARCH_PARAMS_FROM_URL, (state, action: PayloadAction<FromUrlSearchParams> | any) => {
        const params = action.payload;
        let appliedTechFilterValues = state.appliedTechFilterValues;
        const techBreadcrumbs = params.breadcrumbs.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (bc: any) => bc.type === BreadcrumbType.TECH_VALUE,
        ) as TechValueBreadcrumb[];

        if (techBreadcrumbs.length > 0) {
          appliedTechFilterValues = { ...appliedTechFilterValues };
          techBreadcrumbs.forEach((bc) => {
            const code = bc.code + "." + bc.valueType.toLowerCase();
            appliedTechFilterValues[code] = bc.value;
          });
        }

        state.breadcrumbs = params.breadcrumbs;
        state.appliedTechFilterValues = appliedTechFilterValues;
        state.assistantValues = params.assistantValues;
        state.selectedProductGroups = params.productGroups || [];
      })
      .addCase(
        SEARCH_SUCCESS,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<{ data: SearchResultData; selectedCategories: string[] }> | any) => {
          const { data, selectedCategories } = action.payload as {
            data: SearchResultData;
            selectedCategories: string[];
          };

          const dataFacetBreadcrumbs = data.breadcrumbs.filter(isFacetBreacrumb);
          const dataTechValueBreadcrumbs = data.breadcrumbs.filter(isTechValueBreadcrumb);

          const breadcrumbs = state.breadcrumbs.map((item) => {
            if (item.type === BreadcrumbType.FACET) {
              const match = dataFacetBreadcrumbs.find((facet) => facet.facetValueCode === item.facetValueCode);
              if (match) {
                item.name = match.name;
                item.value = match.value;
              }
            } else if (item.type === BreadcrumbType.TECH_VALUE) {
              const codeToFind = item.code + item.valueType.toLowerCase();
              const match = dataTechValueBreadcrumbs.find((facet) => facet.code === codeToFind);
              if (match) {
                item.name = match.name;
              }
            }
            return item;
          });

          state.facetValues = data.facets.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (map: any, facet: any) => {
              map[facet.code] = facet;
              return map;
            },
            {} as Record<string, FacetData>,
          );
          state.selectedProductGroups = selectedCategories;
          state.breadcrumbs = breadcrumbs;
          state.filterConfig = data.filterConfig;
          state.filterConfigImperial = data.filterConfigImperial;
        },
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(TOGGLE_FACET, (state, action: PayloadAction<FacetToggleData> | any) => {
        const data = action.payload as FacetToggleData;
        const newState = data.newValue;

        if (newState) {
          state.breadcrumbs.push({
            type: BreadcrumbType.FACET,
            facetValueCode: data.facetValueCode,
            facetCode: data.facetCode,
            name: data.name,
            value: data.value,
          });
        } else {
          state.breadcrumbs = state.breadcrumbs.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (bc: any) => bc.type !== BreadcrumbType.FACET || bc.facetValueCode !== data.facetValueCode,
          );
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(CLEAR_TECH_VALUES, (state, action: PayloadAction<string[]> | any) => {
        const techFilterUids = action.payload;
        const appliedTechFilterValues = { ...state.appliedTechFilterValues };

        techFilterUids.forEach((code: string) => {
          delete appliedTechFilterValues[code + ".min"];
          delete appliedTechFilterValues[code + ".max"];
        });

        state.appliedTechFilterValues = appliedTechFilterValues;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(REMOVE_BREADCRUMB, (state, action: PayloadAction<BreadcrumbData> | any) => {
        const data = action.payload as BreadcrumbData;
        let appliedTechFilterValues = state.appliedTechFilterValues;

        if (data.type === BreadcrumbType.TECH_VALUE) {
          const fullCode = data.code + "." + data.valueType.toLowerCase();
          appliedTechFilterValues = { ...appliedTechFilterValues };
          delete appliedTechFilterValues[fullCode];

          state.breadcrumbs = state.breadcrumbs.filter(
            (bc) => bc.type === BreadcrumbType.FACET || bc.code !== data.code || bc.valueType !== data.valueType,
          );
        } else {
          state.breadcrumbs = state.breadcrumbs.filter(
            (bc) =>
              bc.type === BreadcrumbType.TECH_VALUE ||
              bc.facetCode !== data.facetCode ||
              bc.facetValueCode !== data.facetValueCode,
          );
        }

        state.appliedTechFilterValues = appliedTechFilterValues;
      })
      .addCase(CLEAR_ALL, (state) => {
        state.breadcrumbs = [];
        state.appliedTechFilterValues = {};
      })

      .addCase(
        APPLY_ALL,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<{ unitMode: UnitMode; values: Record<string, number> }> | any) => {
          const { unitMode, values } = action.payload;
          const differences = diff(state.appliedTechFilterValues, values);

          let differentKeys: string[] = [];
          if (differences) {
            differentKeys = differences.map((diffItem) => diffItem.path![0] as string);
          }

          // Step 1 - remove all Tech Value breadcrumbs which's value has changed
          state.breadcrumbs = state.breadcrumbs.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (bc: any) =>
              bc.type !== BreadcrumbType.TECH_VALUE ||
              differentKeys.indexOf(`${bc.code}.${bc.valueType.toLowerCase()}`) === -1,
          );

          // Step 2 - for each Tech Value, add it if the code and valueType combination is unique
          const config = unitMode === UnitMode.Metric ? state.filterConfig : state.filterConfigImperial;
          if (config) {
            config
              .filter(isTechValueFilterCategory)
              .flatMap((category) => category.items)
              .forEach((filter) => {
                let key = `${filter.uid}.min`;
                let keyChanged = differentKeys.indexOf(key) !== -1;
                const minVal = values[key];
                if (minVal && keyChanged) {
                  state.breadcrumbs.push({
                    type: BreadcrumbType.TECH_VALUE,
                    name: filter.title,
                    value: minVal,
                    valueType: "min",
                    code: filter.uid,
                    uom: getUom(filter.title),
                  });
                }
                key = `${filter.uid}.max`;
                keyChanged = differentKeys.indexOf(key) !== -1;
                const maxVal = values[key];
                if (maxVal && keyChanged) {
                  state.breadcrumbs.push({
                    type: BreadcrumbType.TECH_VALUE,
                    name: filter.title,
                    value: maxVal,
                    valueType: "max",
                    code: filter.uid,
                    uom: getUom(filter.title),
                  });
                }
              });
          }

          state.appliedTechFilterValues = values;
        },
      )
      .addCase(UPDATE_SEARCH_TEXT, (state) => {
        state.breadcrumbs = [];
        state.appliedTechFilterValues = {};
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(SELECT_PRODUCT_GROUP, (state, action: PayloadAction<{ code: string }> | any) => {
        const { code } = action.payload;
        state.selectedProductGroups = code ? [code] : [];
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(SELECT_PRODUCT_GROUP_MULTI, (state, action: PayloadAction<{ code: string }> | any) => {
        const { code } = action.payload;
        if (state.selectedProductGroups.includes(code)) {
          state.selectedProductGroups = state.selectedProductGroups.filter((group) => group !== code);
        } else if (code) {
          state.selectedProductGroups.push(code);
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(SET_SELECTED_PRODUCT_GROUPS, (state, action: PayloadAction<{ values: string[] }> | any) => {
        state.selectedProductGroups = action.payload.values;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(UPDATE_ASSISTANT_VALUES, (state, action: PayloadAction<{ code: string }> | any) => {
        const { code } = action.payload;
        if (state.assistantValues.includes(code)) {
          state.assistantValues = state.assistantValues.filter((value) => value !== code);
        } else {
          state.assistantValues.push(code);
        }
      })

      .addCase(
        UPDATE_IS_SELECTION_ASSISTANT,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<{ value: SelectionAssistantType }> | any) => {
          state.isSelectionAssistant = action.payload.value;
        },
      )
      .addCase(RESET_SA_FILTERS, (state) => {
        state.breadcrumbs = [];
        state.appliedTechFilterValues = {};
        state.selectedProductGroups = [];
        state.assistantValues = [];
      });
  },
});

export const facetsReducer = facetsSlice.reducer;

function isFacetBreacrumb(breadcrumb: BreadcrumbData): breadcrumb is FacetBreadcrumb {
  return breadcrumb.type === BreadcrumbType.FACET;
}

function isTechValueBreadcrumb(breadcrumb: BreadcrumbData): breadcrumb is TechValueBreadcrumb {
  return breadcrumb.type === BreadcrumbType.TECH_VALUE;
}
